// lodash から isEmpty 関数をインポート
import { isEmpty } from 'lodash';
// notistack5 から useSnackbar フックをインポート
import { useSnackbar } from 'notistack5';
// react から useState フックをインポート
import { useState } from 'react';
// react-i18next から useTranslation フックをインポート
import { useTranslation } from 'react-i18next';
// QuestionCompliaEnquete 型をインポート
import { QuestionCompliaEnquete } from 'src/@types/compliaEnquete';
// GeneralEnquete 型をインポート
import { GENERAL_ENQUETE_JSON_INPUT_TYPE, GeneralEnquete, Question } from 'src/@types/generalEnquete';
// OverviewEnquete 型をインポート
import { OverviewEnquete } from 'src/@types/overviewEnquete';
// SeriesAnswerEnquete 型をインポート
import { SeriesAnswerEnquete } from 'src/@types/seriesEnquete';
// useUserApi フックをインポート
import { useUserApi } from 'src/api/useUserApi';
// ANSWER_STATUS 定数と AnswerStatus 型をインポート
import { ANSWER_STATUS, AnswerStatus } from 'src/constants';
// useEnqueteAnswer フックをインポート
import useEnqueteAnswer from 'src/features/general/enquete-answer/hooks/useEnqueteAnswer';
// useEnqueteAnswerGrid フックをインポート
import { useEnqueteAnswerGrid } from 'src/features/general/enquete-answer/hooks/useEnqueteAnswerGrid';
// EnqueteAnswerCompliaEntity などの型をインポート
import {
  EnqueteAnswerCompliaCalendarInfoEntity,
  EnqueteAnswerCompliaEntity,
  EnqueteAnswerCompliaSubmitEmailInfoEntity,
  EnqueteAnswerEntity,
  FileType,
} from 'src/features/general/enquete-answer/store/enqueteAnswerSlice';
// MAX_DATA_SIZE 定数をインポート
import { MAX_DATA_SIZE } from 'src/generalConstants';
// useAuth フックをインポート
import useAuth from 'src/hooks/useAuth';
// useAppSelector フックをインポート
import { useAppSelector } from 'src/redux/store';
// useEnqueteAnswerHeaderInfo フックをインポート
import { useEnqueteAnswerHeaderInfo } from '../hooks/useEnqueteAnswerHeaderInfo';
//一問一答提出ポップアップコンポーネントをインポート
import { TAB_VALUE_COMPLIA as TAB_VALUE } from 'src/constants';

// 回答者ターゲット ID を受け取る useEnqueteAnswerSave フックを定義
const useEnqueteAnswerSave = (respondentTargetId: string) => {
  // 翻訳機能を使用するためのフックを呼び出し
  const { t } = useTranslation();
  // 認証情報からユーザー情報を取得
  const { user } = useAuth();
  // スナックバー通知を使用するためのフックを呼び出し
  const { enqueueSnackbar } = useSnackbar();

  // ユーザー API フックを使用
  const { getByEmail } = useUserApi();
  // アンケート回答グリッド関連の関数を取得
  const {
    saveAnswerComplia,
    tempSaveAnswerComplia,
    updateAnswerComplia,
    tempUpdateAnswerComplia,
    saveAnswer,
    tempSaveAnswer,
    updateAnswer,
    tempUpdateAnswer,
    updateAnswerAction5Complia,
    saveAnswerAction5Complia,
  } = useEnqueteAnswerGrid();
  // 確認履歴を取得するための関数を取得
  const { fetchConfirmHistories } = useEnqueteAnswer();

  // Redux ストアからアンケート回答データを取得
  const enqueteAnswerData = useAppSelector((state) => state.enqueteAnswer.enqueteAnswerData);
  // Redux ストアからアンケート回答が開いているかどうかを取得
  const isEnqueteAnswerOpen = useAppSelector((state) => state.enqueteAnswer.isEnqueteAnswerOpen);

  // 保存中かどうかを示す状態と更新関数を定義
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // 回答ステータスを取得、データが空でなければ status を使用
  const answerStatus = !isEmpty(enqueteAnswerData) ? enqueteAnswerData.status : undefined;
  // すでに回答済みかどうか、またはアンケートが開いていないかを判定
  const isAnswered = [ANSWER_STATUS.ANSWERED].some((v) => v === answerStatus) || !isEnqueteAnswerOpen;

  // Redux から回答者ターゲットデータを取得
  const respondentTargetData = useAppSelector((state) => state.enqueteAnswer.respondentTargetData);
  // アンケート回答依頼取得
  const respondentRequestData = useAppSelector((state) => state.enqueteAnswer.respondentRequestData);
  //組織名とアンケート名取得
  const { enqueteTitleJpn, enqueteTitleEng, enqueteAnswerOrganizationJpn, enqueteAnswerOrganizationEng } =
    useEnqueteAnswerHeaderInfo(respondentRequestData);
  // Redux から現在のタブかどうかを取得
  const currentAnswerTab = useAppSelector((state) => state.enqueteAnswer.currentAnswerTab);
  // Redux ストアから質問配列を取得
  const questions: Question[] = useAppSelector((stateGeneral) => stateGeneral.enqueteGeneralAnswer.questions);
  // Redux ストアから質問アクション⑤配列を取得
  const action5Questions: Question[] = useAppSelector((stateGeneral) => stateGeneral.enqueteGeneralAnswerAction5.questions);
  // Redux ストアからアンケート回答アクション⑤データを取得
  const enqueteAnswerAction5Data = useAppSelector((state) => state.enqueteAnswer.enqueteAnswerAction5Data);
  // 回答アクション⑤ステータスを取得、データが空でなければ status を使用
  const answerAction5Status = !isEmpty(enqueteAnswerAction5Data) ? enqueteAnswerAction5Data.status : undefined;
  // すでに回答アクション⑤回答済みかどうか、またはアンケートが開いていないかを判定
  const isAnsweredAction5 = [ANSWER_STATUS.ANSWERED].some((v) => v === answerAction5Status) || !isEnqueteAnswerOpen;

  // 一般的な保存処理を行う非同期関数を定義
  const commonSaveHandler = async (tabData: GeneralEnquete | SeriesAnswerEnquete | OverviewEnquete, answerStatus: AnswerStatus) => {
    // 保存中状態を true に設定
    setIsSaving(true);

    // ユーザー ID を設定、ユーザーが存在すれば email を使用
    const userId = null !== user ? user.email : '';
    // ユーザーメールを設定、ユーザーが存在すれば email を使用
    const userEmail = null !== user ? user.email : '';

    // メールアドレスでユーザー情報を取得
    const myInfo = await getByEmail(userEmail);
    // ユーザー情報が見つからない場合
    if (!myInfo || myInfo.length === 0) {
      // 保存中状態を false に設定
      setIsSaving(false);
      // ユーザーデータが見つからないエラーをスロー
      throw Error(t('message.exceptionNotFoundUserData'));
    }

    // 保存用のパラメータオブジェクトを作成
    const params: EnqueteAnswerEntity = {
      userId: userId,
      userName: myInfo[0].fullNameJpn,
      userNameEng: myInfo[0].fullNameEng,
      status: answerStatus,
      respondentTargetId: respondentTargetId ?? '',
      answerData: { answerInputData: { tabData: [tabData] } },
      isDeleted: false,
    };

    try {
      // 回答データが最大サイズを超えるかチェック
      if (JSON.stringify(tabData).length > MAX_DATA_SIZE.ANSWER) {
        // データサイズ超過のエラー通知を表示
        enqueueSnackbar(t('message.exceptionDataSizeOver', { target: t('common.answer') }), { variant: 'error' });
      } else {
        // 既存の回答データがあり ID が存在する場合
        if (!isEmpty(enqueteAnswerData) && enqueteAnswerData.id) {
          // 既存の回答 ID を取得
          const answerId = enqueteAnswerData.id;
          // 一時保存か更新かを判定して実行
          answerStatus === ANSWER_STATUS.SAVED ? await tempUpdateAnswer(params, answerId) : await updateAnswer(params, answerId);
          // 確認履歴を取得
          await fetchConfirmHistories(answerId);
        } else {
          // 新規保存か一時保存かを判定して実行
          answerStatus === ANSWER_STATUS.SAVED ? await tempSaveAnswer(params) : await saveAnswer(params);
        }
      }
    } finally {
      // 最終的に保存中状態を false に設定
      setIsSaving(false);
    }
  };

  // Complia アンケートの保存処理を行う非同期関数を定義
  const compliaSaveHandler = async (
    tabData: QuestionCompliaEnquete,
    answerStatus: AnswerStatus,
    files: FileType[],
    deletedFiles: FileType[],
    surveyDetailId: string,
  ) => {
    // 保存中状態を true に設定
    setIsSaving(true);

    // ユーザー ID を設定、ユーザーが存在すれば email を使用
    const userId = null !== user ? user.email : '';
    // ユーザーメールを設定、ユーザーが存在すれば email を使用
    const userEmail = null !== user ? user.email : '';

    // メールアドレスでユーザー情報を取得
    const myInfo = await getByEmail(userEmail);
    // ユーザー情報が見つからない場合
    if (!myInfo || myInfo.length === 0) {
      // 保存中状態を false に設定
      setIsSaving(false);
      // ユーザーデータが見つからないエラーをスロー
      throw Error(t('message.exceptionNotFoundUserData'));
    }

    // 保存用のパラメータオブジェクトを作成
    const params: EnqueteAnswerCompliaEntity = {
      userId: userId,
      userName: myInfo[0].fullNameJpn,
      userNameEng: myInfo[0].fullNameEng,
      status: answerStatus,
      respondentTargetId: respondentTargetId ?? '',
      answerData: { answerInputData: { tabData: [tabData] } },
      isDeleted: false,
      surveyDetailId: surveyDetailId,
    };

    //提出の場合、メールアドレス情報追加
    if (answerStatus === ANSWER_STATUS.ANSWERED) {
      const emailInfo: EnqueteAnswerCompliaSubmitEmailInfoEntity = {
        //メールアドレス見出し
        emailTitle:
          currentAnswerTab === TAB_VALUE.QUESTION_ACTION5
            ? `${enqueteTitleJpn}アクション⑤回答提出完了のお知らせ` + '/' + `${enqueteTitleEng} Action⑤ Submission Completed`
            : `${enqueteTitleJpn}アンケート回答提出完了のお知らせ` + '/' + `${enqueteTitleEng} Submission Completed`,
        //メールアドレス本文
        emailContent:
          currentAnswerTab === TAB_VALUE.QUESTION_ACTION5
            ? `*The English version is provided below the Japanese text.\n\n${enqueteAnswerOrganizationJpn}における${enqueteTitleJpn}のアクション⑤のアンケート提出が完了しました。\n\n回答内容を修正される場合は、内容を上書きのうえ、再度ご提出ください。`
            : `*The English version is provided below the Japanese text.\n\n${enqueteAnswerOrganizationJpn}における${enqueteTitleJpn}のアンケート提出が完了しました。\n\n回答内容を修正される場合は、内容を上書きのうえ、再度ご提出ください。`,
        //メールアドレス本文英語
        emailContentEng:
          currentAnswerTab === TAB_VALUE.QUESTION_ACTION5
            ? `The submission of the ${enqueteTitleEng} Action⑤ for ${enqueteAnswerOrganizationEng} has been successfully submitted.\n\nIf you need to revise your response, please update your answers and click "Submit" again to overwrite your previous submission.`
            : `The submission of the ${enqueteTitleEng} survey for ${enqueteAnswerOrganizationEng} has been completed.\n\nIf you need to revise your response, please update your answers and click "Submit" again to overwrite your previous submission.`,
        //本部コード
        divisionCode: respondentTargetData.organization?.divisionCode,
        //SBUコード
        sbuCode: respondentTargetData.organization?.sbuCode,
        //部コード
        departmentCode: respondentTargetData.organization?.departmentCode,
      };
      params.submitEmailInfo = emailInfo;
    }

    //一時保存の場合、カレンダー編集
    if (answerStatus != ANSWER_STATUS.ANSWERED) {
      //該当タブが「一問一答」の場合
      if (currentAnswerTab === TAB_VALUE.QUESTION) {
        //点検実施期間情報検索
        const question: Question | undefined = questions?.find(
          (item) => item.inputType === GENERAL_ENQUETE_JSON_INPUT_TYPE.DATERANGE && item.validation?.isCalendarSync,
        );
        //特に質問に､「カレンダーへ反映：点検実施期間」がチェックオン、点検実施期間が入力された　の場合、カレンダーへ反映する
        if (question && !!question.answerItem?.length) {
          const calendarInfo: EnqueteAnswerCompliaCalendarInfoEntity = {
            //適用開始日
            startDate: question.answerItem != undefined && question.answerItem[0].value ? getDate(question.answerItem[0].value) : undefined,
            //適用終了日
            endDate: question.answerItem != undefined && question.answerItem[1].value ? getDate(question.answerItem[1].value) : undefined,
            //画面から本部極担当者名（日本語）
            userNameJpn: myInfo[0].fullNameJpn,
            //画面から本部極担当者名（英語）
            userNameEng: myInfo[0].fullNameEng,
            //画面から本部極担当者のメールアドレス
            email: myInfo[0].email,
            //画面から本部極担当者のBluePageメールアドレス
            bluePageEmailAddress: myInfo[0].bluePageEmailAddress,
            //本部コード
            divisionCode: respondentTargetData.organization?.divisionCode,
            //SBUコード
            sbuCode: respondentTargetData.organization?.sbuCode,
            //部コード
            departmentCode: respondentTargetData.organization?.departmentCode,
          };
          params.calendarInfo = calendarInfo;
        }
        //該当タブが「一問一答アクション⑤」の場合
      } else if (currentAnswerTab === TAB_VALUE.QUESTION_ACTION5) {
        //現地現物確認予定日情報検索
        const question: Question | undefined = action5Questions?.find(
          (item) => item.inputType === GENERAL_ENQUETE_JSON_INPUT_TYPE.DATE && item.validation?.isCalendarSync,
        );
        //特に質問に､「カレンダーへ反映：点検実施期間」がチェックオン、点検実施期間が入力された　の場合、カレンダーへ反映する
        if (question && !!question.answerItem?.length) {
          const calendarInfo: EnqueteAnswerCompliaCalendarInfoEntity = {
            //適用開始日
            startDate: question.answerItem != undefined && question.answerItem[0].value ? getDate(question.answerItem[0].value)  : undefined,
            //適用終了日
            endDate: question.answerItem != undefined && question.answerItem[0].value ? getDate(question.answerItem[0].value)  : undefined,
          };
          params.calendarInfo = calendarInfo;
        }
      }
    }

    try {
      // 回答データが最大サイズを超えるかチェック
      if (JSON.stringify(tabData).length > MAX_DATA_SIZE.ANSWER) {
        // データサイズ超過のエラー通知を表示
        enqueueSnackbar(t('message.exceptionDataSizeOver', { target: t('common.answer') }), { variant: 'error' });
        return false;
        //該当タブが「一問一答」の場合
      } else if (currentAnswerTab === TAB_VALUE.QUESTION) {
        // 既存の回答データがあり ID が存在する場合
        if (!isEmpty(enqueteAnswerData) && enqueteAnswerData.id) {
          // 既存の回答 ID を取得
          const answerId = enqueteAnswerData.id;
          // 一時更新か更新かを判定して実行、ファイル情報も渡す
          const success =
            answerStatus === ANSWER_STATUS.SAVED
              ? await tempUpdateAnswerComplia(params, answerId, files, deletedFiles)
              : await updateAnswerComplia(params, answerId, files, deletedFiles);
          // 確認履歴を取得
          await fetchConfirmHistories(answerId);
          return success;
        } else {
          // 新規保存か一時保存かを判定して実行、ファイル情報も渡す
          return answerStatus === ANSWER_STATUS.SAVED
            ? await tempSaveAnswerComplia(params, files, deletedFiles)
            : await saveAnswerComplia(params, files, deletedFiles);
        }
        //該当タブが「一問一答アクション⑤」の場合
      } else if (currentAnswerTab === TAB_VALUE.QUESTION_ACTION5) {
        // 既存の回答データがあり ID が存在する場合
        if (enqueteAnswerAction5Data && enqueteAnswerAction5Data.id) {
          //一時更新か更新、ファイル情報も渡す
          return await updateAnswerAction5Complia(params, enqueteAnswerAction5Data.id, files, deletedFiles, answerStatus != ANSWER_STATUS.ANSWERED);
        } else {
          //新規保存か一時保存、ファイル情報も渡す
          return await saveAnswerAction5Complia(params, files, deletedFiles, answerStatus != ANSWER_STATUS.ANSWERED);
        }
      }
    } finally {
      // 最終的に保存中状態を false に設定
      setIsSaving(false);
    }
  };

  /**
   * 年月日を切り出す
   * @param currentDate 
   * @returns 
   */
  const getDate = (currentDate : any) => {
    if (currentDate) {
      return String(currentDate).substring(0, 10);
    }

    return currentDate;
  }

  // 状態とハンドラー関数をオブジェクトとして返す
  return {
    isSaving,
    isAnswered,
    commonSaveHandler,
    compliaSaveHandler,
    isAnsweredAction5,
  };
};

// useEnqueteAnswerSave フックをデフォルトエクスポート
export default useEnqueteAnswerSave;
